import * as React from 'react';
import styled from 'styled-components';

import { getSemanticAttributes } from '../../internals/semanticProps';
import { Spinner } from '../../primitives/Spinner';
import { rem } from '../../tokens/spacing';

export interface ImportFileInputProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> {
  /** DOM / automation id — sets `id` on the hidden file input. */
  semanticId: string;
  accept?: string;
  label: string;
  loading?: boolean;
  icon?: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

function toLegacyContainerSuffix(value: string): string {
  return value
    ?.replace(/[^a-zA-Z0-9\s]/g, '')
    ?.split(/\s+/)
    ?.map((word, index) => {
      if (index === 0) {
        return word?.toLowerCase();
      }
      return word?.charAt(0)?.toUpperCase() + word?.slice(1)?.toLowerCase();
    })
    ?.join('');
}

const StyledRoot = styled.div`
  position: relative;
  width: 100%;
  box-sizing: border-box;
  cursor: pointer;
`;

/** Visually hidden — activation is via container click (see handleContainerClick). */
const StyledHiddenInput = styled.input`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

const StyledTrigger = styled.span`
  display: block;
  width: 100%;
  box-sizing: border-box;
  cursor: pointer;
`;

const StyledLoadingRow = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${rem(8)};
  width: 100%;

  .spinner-container {
    flex-shrink: 0;
    width: ${rem(18)};
    height: ${rem(18)};
  }
`;

export const ImportFileInput = React.forwardRef<HTMLDivElement, ImportFileInputProps>(
  function ImportFileInput(
    {
      semanticId,
      accept = '.xlsx, .xls',
      label,
      loading = false,
      icon,
      onChange,
      className,
      onClick,
      ...rest
    },
    ref,
  ) {
    const inputRef = React.useRef<HTMLInputElement>(null);
    const semanticProps = getSemanticAttributes(semanticId);
    const containerClassName = [
      'input-file-container',
      'import-container',
      `${toLegacyContainerSuffix(semanticId)}Container`,
      className,
    ]
      .filter(Boolean)
      .join(' ');

    const openFileDialog = () => {
      if (loading) return;
      inputRef.current?.click();
    };

    const handleContainerClick = (event: React.MouseEvent<HTMLDivElement>) => {
      event.stopPropagation();
      openFileDialog();
      onClick?.(event);
    };

    const handleContainerKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        openFileDialog();
      }
    };

    return (
      <StyledRoot
        ref={ref}
        className={containerClassName}
        role="button"
        tabIndex={loading ? -1 : 0}
        aria-disabled={loading || undefined}
        onClick={handleContainerClick}
        onKeyDown={handleContainerKeyDown}
        {...rest}
      >
        <StyledHiddenInput
          ref={inputRef}
          className="input-file"
          type="file"
          accept={accept}
          disabled={loading}
          onChange={onChange}
          tabIndex={-1}
          aria-hidden
          {...semanticProps}
        />
        <StyledTrigger className="text-center dt-btn dt-btn-outline-primary dt-btn-sm input-file-trigger sm:text-left import-label">
          {loading ? (
            <StyledLoadingRow className="flex items-center gap-2">
              <Spinner size="sm" className="spinner-container" />
              {label}
            </StyledLoadingRow>
          ) : (
            <>
              {icon ? <span className="material-symbols-outlined">{icon}</span> : null} {label}
            </>
          )}
        </StyledTrigger>
      </StyledRoot>
    );
  },
);

ImportFileInput.displayName = 'ImportFileInput';
